import {
  TypographyCaption,
  TypographyH2,
  TypographyP2,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';

import {
  AgeNumberInput,
  FrontBackToggle,
  OnsetIcon,
} from './VisualDxDiagnosticFlow.atoms';
import {
  AGE_BAND_OPTIONS,
  MORPHOLOGY_OPTIONS,
  ONSET_OPTIONS,
  REGION_OPTIONS,
  type AgeBand,
  type BodyAspect,
  type Morphology,
  type OnsetSpeed,
  type Region,
} from './VisualDxDiagnosticFlow.constants';
import { BodySilhouette } from './VisualDxDiagnosticFlow.silhouette';
import {
  chipClass,
  regionGridWrapClass,
  regionStepLayoutClass,
  rowCardClass,
  silhouetteWrapClass,
  stepHeadingWrapClass,
} from './VisualDxDiagnosticFlow.styles';

/** Step + summary sub-components for the inline VisualDx diagnostic flow. */

const StepHeading = ({ title, subtitle }: { title: string; subtitle: string }) => (
  <div className={stepHeadingWrapClass}>
    <TypographyH2 className="mb-1.5 text-text-primary">{title}</TypographyH2>
    <TypographyP2 className="text-text-secondary">{subtitle}</TypographyP2>
  </div>
);

// ── Step 1: Region (Front/Back + 12-region multi-select grid) ────────

export const RegionStep = ({
  bodyAspect,
  selected,
  onAspectChange,
  onToggle,
}: {
  bodyAspect: BodyAspect;
  selected: Set<Region>;
  onAspectChange: (next: BodyAspect) => void;
  onToggle: (id: Region) => void;
}) => (
  <div>
    <StepHeading
      title="Where is it located?"
      subtitle="Tap a region on the silhouette or pick from the list."
    />
    <FrontBackToggle value={bodyAspect} onChange={onAspectChange} />
    {/* Silhouette + grid share the same `selected` Set, so toggling on
     *  either input updates the other automatically. */}
    <div className={regionStepLayoutClass}>
      <div className={silhouetteWrapClass}>
        <BodySilhouette
          bodyAspect={bodyAspect}
          selectedRegions={selected}
          onToggleRegion={onToggle}
        />
      </div>
      <div className={regionGridWrapClass}>
        {REGION_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => onToggle(opt.id)}
            className={chipClass(selected.has(opt.id))}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  </div>
);

// ── Step 2: Morphology (wrap pills, multi-select) ────────────────────

export const MorphologyStep = ({
  selected,
  onToggle,
}: {
  selected: Set<Morphology>;
  onToggle: (id: Morphology) => void;
}) => (
  <div>
    <StepHeading
      title="Which findings are present?"
      subtitle="Tick any findings you see on exam."
    />
    <div className="flex flex-wrap justify-center gap-2.5">
      {MORPHOLOGY_OPTIONS.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onToggle(opt.id)}
          className={cn(chipClass(selected.has(opt.id)), 'rounded px-3.5 py-2')}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

// ── Step 3: Age (band picker + exact-age input) ──────────────────────

export const AgeStep = ({
  band,
  exactYears,
  onBandChange,
  onExactYearsChange,
}: {
  band: AgeBand | null;
  exactYears: string;
  onBandChange: (next: AgeBand) => void;
  onExactYearsChange: (next: string) => void;
}) => (
  <div>
    <StepHeading
      title="What's the patient's age?"
      subtitle="Pick an age band or enter an exact age."
    />
    <TypographyCaption className="mb-2 text-text-secondary">Age band</TypographyCaption>
    <div className="mb-[18px] flex flex-wrap gap-2">
      {AGE_BAND_OPTIONS.map((opt) => {
        const active = band === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onBandChange(opt.id)}
            className={cn(
              chipClass(active),
              'flex min-w-[92px] flex-col gap-0.5 px-3.5 py-2.5'
            )}
          >
            <span className="text-sm font-semibold">{opt.label}</span>
            <span
              className={cn(
                'text-[11px] font-normal',
                active ? 'text-text-active' : 'text-text-secondary'
              )}
            >
              {opt.range}
            </span>
          </button>
        );
      })}
    </div>
    <TypographyCaption className="mb-1.5 tracking-[0.06em] text-text-secondary">
      OR ENTER EXACT AGE
    </TypographyCaption>
    <div className="flex items-center gap-2">
      <AgeNumberInput value={exactYears} onChange={onExactYearsChange} />
      <TypographyP2 className="text-text-secondary">years</TypographyP2>
    </div>
  </div>
);

// ── Step 4: Onset (5 vertical cards w/ line-graph icons) ─────────────

export const OnsetStep = ({
  selected,
  onSelect,
}: {
  selected: OnsetSpeed | null;
  onSelect: (id: OnsetSpeed) => void;
}) => (
  <div>
    <StepHeading
      title="How did the condition start?"
      subtitle="This helps VisualDx weight differentials by tempo."
    />
    <div className="flex flex-col gap-2.5">
      {ONSET_OPTIONS.map((opt) => {
        const active = selected === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onSelect(opt.id)}
            className={rowCardClass(active)}
          >
            <div className="flex w-8 justify-center">
              <OnsetIcon kind={opt.id} />
            </div>
            <div className="flex flex-col gap-0.5">
              <TypographyP2 className="font-semibold text-text-primary">
                {opt.label}
              </TypographyP2>
              <TypographyCaption className="text-text-secondary">
                {opt.hint}
              </TypographyCaption>
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

/* ReviewSummary lives in ./VisualDxDiagnosticFlow.review.tsx to keep
 * this input-step file under the 300-line cap. */
